import { useCallback, useEffect, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent, KeyboardEvent, SyntheticEvent } from 'react';
import SectionHeading from '@/components/base/SectionHeading';

const BEFORE_IMG = 'https://storage.helloreaddy.io/project_files/2794a53c-a8d9-4b75-bc9f-61e20b4d813d/3d88e2bf-44d0-410f-ac7d-be0e00ff0a1f_compressed_unnamed.webp';
const AFTER_IMG = 'https://storage.helloreaddy.io/project_files/2794a53c-a8d9-4b75-bc9f-61e20b4d813d/9898d945-0fc4-4ad0-85bd-84902e970dae_compressed_unnamed.webp';

const tag = 'absolute top-4 z-10 rounded-full bg-background-50/90 border border-background-300 px-3 py-1 text-xs font-medium tracking-[0.12em] pointer-events-none';

const ease = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - ((-2 * t + 2) ** 3) / 2);
const clamp = (v: number) => Math.min(100, Math.max(0, v));

type ImgKey = 'before' | 'after';

export default function Results() {
  const stageRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState(50);
  const dragging = useRef(false);
  const rafRef = useRef<number | null>(null);
  const played = useRef(false);
  const readyMap = useRef<Record<ImgKey, boolean>>({ before: false, after: false });
  const [ready, setReady] = useState(false);
  const failed = useRef(false);

  const stopAuto = () => {
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
  };

  // Both images must be loaded AND decoded before the auto demo may run.
  const markReady = (key: ImgKey) => (e: SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    const done = () => {
      readyMap.current[key] = true;
      if (readyMap.current.before && readyMap.current.after && !failed.current) setReady(true);
    };
    if (typeof img.decode === 'function') img.decode().then(done).catch(done);
    else done();
  };
  const markFailed = () => { failed.current = true; played.current = true; };

  const updateFromX = useCallback((clientX: number) => {
    const el = stageRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos(clamp(((clientX - r.left) / r.width) * 100));
  }, []);

  useEffect(() => {
    const el = stageRef.current;
    if (!el || !ready || played.current) return undefined;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return undefined;
    const io = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting || played.current) return;
      played.current = true;
      io.disconnect();
      // before (100) -> after (0) -> settle in the middle (50)
      const steps = [
        { from: 50, to: 100, dur: 700 },
        { from: 100, to: 0, dur: 1600 },
        { from: 0, to: 50, dur: 900 },
      ];
      let i = 0;
      let start = 0;
      const tick = (now: number) => {
        if (!start) start = now;
        const s = steps[i];
        const t = Math.min(1, (now - start) / s.dur);
        setPos(s.from + (s.to - s.from) * ease(t));
        if (t >= 1) {
          i += 1;
          start = 0;
          if (i >= steps.length) { rafRef.current = null; return; }
        }
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    }, { threshold: 0.55 });
    io.observe(el);
    return () => { io.disconnect(); stopAuto(); };
  }, [ready]);

  const onDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    stopAuto();
    played.current = true;
    dragging.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
    updateFromX(e.clientX);
  };
  const onMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (dragging.current) updateFromX(e.clientX);
  };
  const onUp = () => { dragging.current = false; };
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const stepSize = e.shiftKey ? 10 : 5;
    let next: number | null = null;
    if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') next = pos - stepSize;
    else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') next = pos + stepSize;
    else if (e.key === 'PageDown') next = pos - 20;
    else if (e.key === 'PageUp') next = pos + 20;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = 100;
    if (next === null) return;
    e.preventDefault();
    stopAuto();
    played.current = true;
    setPos(clamp(next));
  };

  const rounded = Math.round(pos);

  return (
    <section className="py-16 md:py-20 lg:py-[112px] px-4 md:px-6 lg:px-20">
      <div className="max-w-content mx-auto">
        <SectionHeading center eyebrow="SEE THE DIFFERENCE" title="Results You Can See. Comfort You Can Feel." />
        <div
          ref={stageRef}
          role="slider"
          tabIndex={0}
          aria-label="Before and after comparison. Use arrow keys to reveal more of the before or after photo."
          aria-orientation="horizontal"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={rounded}
          aria-valuetext={`${rounded}% before photo visible`}
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerCancel={onUp}
          onKeyDown={onKey}
          className="relative mt-12 md:mt-16 w-full h-[360px] md:h-[480px] lg:h-[620px] rounded-[20px] overflow-hidden border border-background-300 bg-background-200 cursor-ew-resize select-none touch-pan-y outline-none focus-visible:ring-2 focus-visible:ring-secondary-500 focus-visible:ring-offset-4 focus-visible:ring-offset-background-50"
        >
          <img loading="lazy" decoding="async" src={AFTER_IMG} onLoad={markReady('after')} onError={markFailed} alt="Bright clean kitchen after a CLEARY clean" title="After cleaning — CLEARY" draggable={false} className="absolute inset-0 w-full h-full object-cover object-center pointer-events-none" />
          <img loading="lazy" decoding="async" src={BEFORE_IMG} onLoad={markReady('before')} onError={markFailed} alt="Messy kitchen before cleaning" title="Before cleaning — CLEARY" draggable={false} className="absolute inset-0 w-full h-full object-cover object-center pointer-events-none" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }} />
          <span className={`${tag} left-4`}>BEFORE</span>
          <span className={`${tag} right-4`}>AFTER</span>
          <div className="absolute inset-y-0 w-0.5 -translate-x-1/2 bg-background-50 z-10 pointer-events-none" style={{ left: `${pos}%` }}>
            <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-background-50 border border-background-300 flex items-center justify-center text-foreground-950"><i className="ri-arrow-left-right-line" /></span>
          </div>
        </div>
      </div>
    </section>
  );
}